import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { todayISO } from '../../utils/dates';
import { splitHoursMinutes, combineHoursMinutes } from '../../utils/timesheet-capacity';
import {
  getWorklogCategories,
  type WorklogCategory,
} from '../../api/worklog-category.api';

// ─── Props ────────────────────────────────────────────────────────────────────

interface WorklogFormProps {
  onSubmit: (data: { date: string; hours: number; description: string; worklogCategoryId?: string }) => void;
  isSaving?: boolean;
  initialValues?: {
    date: string;
    hours: number;
    description: string;
    worklogCategoryId?: string | null;
  };
}

// ─── Helpers ──────────────────────────────────────────────────────────────────

const MONTHS = [
  'Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun',
  'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec',
];

function parseInitialDate(iso: string): { year: number; month: number; day: number } {
  const parts = iso.split('-');
  return {
    year: parseInt(parts[0], 10) || new Date().getFullYear(),
    month: parseInt(parts[1], 10) || new Date().getMonth() + 1,
    day: parseInt(parts[2], 10) || new Date().getDate(),
  };
}

function daysInMonth(year: number, month: number): number {
  return new Date(year, month, 0).getDate();
}

function toISO(year: number, month: number, day: number): string {
  return `${year}-${String(month).padStart(2, '0')}-${String(day).padStart(2, '0')}`;
}

function isMobileDevice(): boolean {
  if (typeof window === 'undefined') return false;
  return /Android|iPhone|iPad|iPod|webOS|BlackBerry|IEMobile|Opera Mini/i.test(
    navigator.userAgent,
  ) || window.innerWidth <= 480;
}

// ─── Component ────────────────────────────────────────────────────────────────

const WorklogForm: React.FC<WorklogFormProps> = ({
  onSubmit,
  isSaving = false,
  initialValues,
}) => {
  const isMobile = useMemo(() => isMobileDevice(), []);

  const initial = parseInitialDate(initialValues?.date ?? todayISO());
  const [date, setDate] = useState(initialValues?.date ?? todayISO());
  const [year, setYear] = useState(initial.year);
  const [month, setMonth] = useState(initial.month);
  const [day, setDay] = useState(initial.day);

  // Hours + minutes as separate inputs
  const initialHM = initialValues ? splitHoursMinutes(initialValues.hours) : { h: '', m: '' };
  const [hoursInput, setHoursInput] = useState(initialHM.h);
  const [minutesInput, setMinutesInput] = useState(initialHM.m);

  const [description, setDescription] = useState(
    initialValues?.description ?? '',
  );
  const [categories, setCategories] = useState<WorklogCategory[]>([]);
  const [selectedCategoryId, setSelectedCategoryId] = useState<string>(
    initialValues?.worklogCategoryId ?? '',
  );
  const [error, setError] = useState<string | null>(null);

  // Fetch active categories on mount
  useEffect(() => {
    getWorklogCategories(false).then((cats) => {
      setCategories(cats);
      // Pre-select the default category if no initial value
      if (!initialValues?.worklogCategoryId && cats.length > 0) {
        const def = cats.find((c) => c.isDefault) ?? cats[0];
        setSelectedCategoryId(def.id);
      }
    }).catch(() => {
      // Categories are optional — form still works without them
    });
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const maxDay = daysInMonth(year, month);
  const currentYear = new Date().getFullYear();

  const totalHours = combineHoursMinutes(hoursInput, minutesInput);

  const handleSubmit = useCallback(
    (e: React.FormEvent) => {
      e.preventDefault();
      setError(null);

      if (totalHours <= 0) {
        setError('Time must be greater than zero');
        return;
      }
      if (totalHours > 24) {
        setError('Time cannot exceed 24 hours');
        return;
      }

      const resolvedDate = isMobile
        ? toISO(year, month, Math.min(day, maxDay))
        : date;

      if (!resolvedDate) {
        setError('Date is required');
        return;
      }

      onSubmit({
        date: resolvedDate,
        hours: totalHours,
        description: description.trim(),
        worklogCategoryId: selectedCategoryId || undefined,
      });

      if (!initialValues) {
        setHoursInput('');
        setMinutesInput('');
        setDescription('');
      }
    },
    [isMobile, date, year, month, day, maxDay, totalHours, description, selectedCategoryId, onSubmit, initialValues],
  );

  return (
    <form className="rp-worklog-form" onSubmit={handleSubmit}>
      <div className="rp-worklog-form__field">
        <label className="rp-worklog-form__label">Date</label>
        {isMobile ? (
          <div className="rp-worklog-form__date-selects">
            <select
              value={day}
              onChange={(e) => setDay(Number(e.target.value))}
              className="rp-worklog-form__select rp-worklog-form__select--day"
            >
              {Array.from({ length: maxDay }, (_, i) => i + 1).map((d) => (
                <option key={d} value={d}>{d}</option>
              ))}
            </select>
            <select
              value={month}
              onChange={(e) => {
                const m = Number(e.target.value);
                setMonth(m);
                const max = daysInMonth(year, m);
                if (day > max) setDay(max);
              }}
              className="rp-worklog-form__select rp-worklog-form__select--month"
            >
              {MONTHS.map((name, i) => (
                <option key={i} value={i + 1}>{name}</option>
              ))}
            </select>
            <select
              value={year}
              onChange={(e) => {
                const y = Number(e.target.value);
                setYear(y);
                const max = daysInMonth(y, month);
                if (day > max) setDay(max);
              }}
              className="rp-worklog-form__select rp-worklog-form__select--year"
            >
              {Array.from({ length: 5 }, (_, i) => currentYear - 2 + i).map((y) => (
                <option key={y} value={y}>{y}</option>
              ))}
            </select>
          </div>
        ) : (
          <input
            type="date"
            value={date}
            onChange={(e) => setDate(e.target.value)}
            className="rp-worklog-form__date-input"
          />
        )}
      </div>

      <div className="rp-worklog-form__row">
        <div className="rp-worklog-form__field rp-worklog-form__field--grow">
          <label className="rp-worklog-form__label">Time Spent</label>
          <div className="rp-worklog-form__time-row">
            <input
              type="number"
              min={0}
              max={24}
              value={hoursInput}
              onChange={(e) => setHoursInput(e.target.value)}
              className="rp-worklog-form__time-input"
              placeholder="0"
            />
            <span className="rp-worklog-form__time-unit">h</span>
            <input
              type="number"
              min={0}
              max={59}
              step={5}
              value={minutesInput}
              onChange={(e) => setMinutesInput(e.target.value)}
              className="rp-worklog-form__time-input"
              placeholder="0"
            />
            <span className="rp-worklog-form__time-unit">m</span>
          </div>
        </div>
      </div>

      {categories.length > 1 && (
        <div className="rp-worklog-form__field">
          <label className="rp-worklog-form__label">Category</label>
          <select
            value={selectedCategoryId}
            onChange={(e) => setSelectedCategoryId(e.target.value)}
            className="rp-worklog-form__select rp-worklog-form__select--category"
          >
            {categories.map((cat) => (
              <option key={cat.id} value={cat.id}>
                {cat.name}{cat.isBillable ? ' ($)' : ''}
              </option>
            ))}
          </select>
        </div>
      )}

      <div className="rp-worklog-form__field">
        <label className="rp-worklog-form__label">
          Description (optional)
        </label>
        <input
          type="text"
          value={description}
          onChange={(e) => setDescription(e.target.value)}
          placeholder="What did you work on?"
          className="rp-worklog-form__text-input"
        />
      </div>

      {error && (
        <p className="rp-worklog-form__error">{error}</p>
      )}

      <div className="rp-worklog-form__actions">
        <button
          type="submit"
          disabled={isSaving}
          className="rp-worklog-form__submit"
        >
          {isSaving ? 'Saving...' : initialValues ? 'Update' : 'Log Time'}
        </button>
      </div>
    </form>
  );
};

export default WorklogForm;
